import React, { useState, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { VehicleDataTable } from '../table/VehicleDataTable';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import type { SIFTVehicle, UserVinStatus } from '../../types/sift';
import { siftApiClient } from '../../api/siftApiClient';
import { apiVehiclesToSIFTVehicles } from '../../api/dataAdapter';
import { formatIngestAge, formatIngestTs } from '../shared/dataFreshness';
import './UnifiedDashboard.css';

// Action filter definitions - driven by the vin_action_filter column from Snowflake.
// Ordered by urgency to match VIN_ACTION_FILTER_RANK in parquet_cache_service.py.
// EXCLUDE is intentionally absent: those rows are filtered out server-side.
const ACTION_FILTERS = [
  { 
    label: 'Ready to List', 
    key: 'READY TO LIST', 
    color: 'var(--color-primary, #FFC72C)',
  },
  { 
    label: 'Auto-Launch', 
    key: 'AUTO-LISTED', 
    color: 'var(--color-primary, #FFC72C)',
  },
  { 
    label: 'Expired Listings', 
    key: 'EXPIRED LISTINGS', 
    color: 'var(--color-primary, #FFC72C)',
  },
  { 
    label: 'Active >3 Day', 
    key: 'ACTIVE >3 DAY', 
    color: 'var(--color-primary, #FFC72C)',
  },
  { 
    label: 'Potential for Inspection', 
    key: 'POTENTIAL FOR INSPECTION', 
    color: 'var(--color-primary, #FFC72C)',
  },
  { 
    label: 'Other', 
    key: 'OTHER', 
    color: 'var(--color-primary, #FFC72C)',
  }
];

interface UnifiedDashboardProps {
  userRole?: string;
  impersonatedRole?: string | null;
  territoryAccess?: {
    is_admin: boolean;
    access_level: string | null;
    territory_value: string | null;
  };
  drilldownFilter?: {
    sellerId: string;
    sellerName: string;
    actionFilter?: string;
  } | null;
  onDrilldownConsumed?: () => void;
  /** Mia's "Where to focus" in the VIN modal (RDS-3.5), gated by the rds_mia_agent flag. */
  miaEnabled?: boolean;
}

export const UnifiedDashboard: React.FC<UnifiedDashboardProps> = ({ 
  userRole = 'default',
  impersonatedRole,
  drilldownFilter,
  onDrilldownConsumed,
  miaEnabled = false,
}) => {
  const [siftVehicles, setSiftVehicles] = useState<SIFTVehicle[]>([]);
  const [activeActionFilter, setActiveActionFilter] = useState<string | null>(null);
  const [selectedAgingRanges, setSelectedAgingRanges] = useState<string[]>([]);
  const [selectedExpiredRanges, setSelectedExpiredRanges] = useState<string[]>([]);
  const [selectedDaysSinceInspectionRanges, setSelectedDaysSinceInspectionRanges] = useState<string[]>([]);
  const [selectedInspectStatuses, setSelectedInspectStatuses] = useState<string[]>([]);
  const [selectedUserStatuses, setSelectedUserStatuses] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(''); // Input value
  const [activeSearch, setActiveSearch] = useState<string>(''); // Applied search sent to API
  const [filteredVehicles, setFilteredVehicles] = useState<SIFTVehicle[]>([]);
  const [initialLoading, setInitialLoading] = useState(true); // Initial page load
  const [isDataLoading, setIsDataLoading] = useState(false); // Filter/pagination triggered loading
  const [error, setError] = useState<string | null>(null);
  const [filterCounts, setFilterCounts] = useState<any>(null);
  
  // Panel collapse state
  const [isFilterPaneCollapsed, setIsFilterPaneCollapsed] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Column filter state (for table column dropdowns). Site Location defaults to
  // Offsite: onsite units are not actionable from this board.
  const [columnFilters, setColumnFilters] = useState<Record<string, Set<string>>>({
    site_location: new Set(['Offsite']),
  });
  
  // Selected VINs state (for bulk status assignment)
  const [selectedVins, setSelectedVins] = useState<Set<string>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [vinDataUpdated, setVinDataUpdated] = useState<string | null>(null);
  const recordsPerPage = 100;
  
  // Sort state - now supports multiple columns
  const [sortColumns, setSortColumns] = useState<Array<{ key: string; order: 'asc' | 'desc' }>>([
    { key: 'days_listed', order: 'desc' }
  ]);
  
  // Optional filters state - now arrays for multi-select
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
  const [selectedMarketCenters, setSelectedMarketCenters] = useState<string[]>([]);
  const [selectedRdsTerritories, setSelectedRdsTerritories] = useState<string[]>([]);
  const [selectedSellers, setSelectedSellers] = useState<string[]>([]);
  
  // Raw filter options (all possible values)
  const [allManagers, setAllManagers] = useState<string[]>([]);
  const [allMarketCenters, setAllMarketCenters] = useState<string[]>([]);
  const [allRdsTerritories, setAllRdsTerritories] = useState<string[]>([]);
  const [allSellers, setAllSellers] = useState<string[]>([]);
  
  // Mapping data for cascading filters
  const [filterMapping, setFilterMapping] = useState<{
    manager: string;
    territory_market_center: string;
    rds_territory: string;
    seller_name: string;
  }[]>([]);

  // Cascading filter options - each dropdown shows all options,
  // but cross-filters based on OTHER dropdowns' selections (not its own)
  const filteredOptions = useMemo(() => {
    // Helper: filter mappings by all selections EXCEPT the excluded one
    const filterMappingsExcluding = (exclude: 'manager' | 'marketCenter' | 'rdsTerritory' | 'seller') => {
      let mappings = filterMapping;
      if (exclude !== 'manager' && selectedManagers.length > 0) {
        mappings = mappings.filter(m => selectedManagers.includes(m.manager));
      }
      if (exclude !== 'marketCenter' && selectedMarketCenters.length > 0) {
        mappings = mappings.filter(m => selectedMarketCenters.includes(m.territory_market_center));
      }
      if (exclude !== 'rdsTerritory' && selectedRdsTerritories.length > 0) {
        mappings = mappings.filter(m => selectedRdsTerritories.includes(m.rds_territory));
      }
      if (exclude !== 'seller' && selectedSellers.length > 0) {
        mappings = mappings.filter(m => selectedSellers.includes(m.seller_name));
      }
      return mappings;
    };

    // Each dropdown's options are filtered by all OTHER selections
    const managerMappings = filterMappingsExcluding('manager');
    const marketCenterMappings = filterMappingsExcluding('marketCenter');
    const rdsTerritoryMappings = filterMappingsExcluding('rdsTerritory');
    const sellerMappings = filterMappingsExcluding('seller');

    const managers = [...new Set(managerMappings.map(m => m.manager))].filter(Boolean).sort();
    const marketCenters = [...new Set(marketCenterMappings.map(m => m.territory_market_center))].filter(Boolean).sort();
    const rdsTerritories = [...new Set(rdsTerritoryMappings.map(m => m.rds_territory))].filter(Boolean).sort();
    const sellers = [...new Set(sellerMappings.map(m => m.seller_name))].filter(Boolean).sort();

    return {
      managers: managers.length > 0 ? managers : allManagers,
      marketCenters: marketCenters.length > 0 ? marketCenters : allMarketCenters,
      rdsTerritories: rdsTerritories.length > 0 ? rdsTerritories : allRdsTerritories,
      sellers: sellers.length > 0 ? sellers : allSellers
    };
  }, [filterMapping, selectedManagers, selectedMarketCenters, selectedRdsTerritories, selectedSellers, allManagers, allMarketCenters, allRdsTerritories, allSellers]);

  // Load filter options on component mount or when impersonation changes
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setInitialLoading(true);
        
        // Reset selections when impersonation changes (now arrays)
        setSelectedManagers([]);
        setSelectedMarketCenters([]);
        setSelectedRdsTerritories([]);
        setSelectedSellers([]);
        
        // Load filter options from backend (pass impersonated role if set)
        const filterOptions = await siftApiClient.getFilterOptions(impersonatedRole || undefined);
        
        // Store all options (raw lists)
        setAllManagers(filterOptions.managers || []);
        setAllMarketCenters(filterOptions.market_centers || []);
        setAllRdsTerritories(filterOptions.rds_territories || []);
        setAllSellers(filterOptions.sellers || []);
        
        // Store mapping for cascading filters
        setFilterMapping(filterOptions.mapping || []);
        
        // Apply default filter based on user role
        // Role string length determines territory level:
        //   10 chars = Manager (grandparent) → default Manager filter
        //   13 chars = Market Center (parent) → default Manager filter
        //   16-17 chars = RDS Territory → default RDS Territory filter
        //   "admin" = no default
        const effectiveRole = impersonatedRole || userRole;
        if (effectiveRole && effectiveRole !== 'admin' && effectiveRole !== 'default') {
          const roles = effectiveRole.split(',').map((r: string) => r.trim());
          const mapping = filterOptions.mapping || [];
          
          // Check first role to determine level (multi-role defaults to first)
          const primaryRole = roles[0];
          const roleLen = primaryRole.length;
          
          if (roleLen === 16 || roleLen === 17) {
            // Territory level → default both Manager and RDS Territory filters
            const matchingTerritories = new Set<string>();
            const matchingManagers = new Set<string>();
            for (const role of roles) {
              mapping.forEach((m: any) => {
                if (m.territory_id === role) {
                  if (m.rds_territory) matchingTerritories.add(m.rds_territory);
                  if (m.manager) matchingManagers.add(m.manager);
                }
              });
            }
            if (matchingTerritories.size > 0) {
              setSelectedRdsTerritories(Array.from(matchingTerritories));
            }
            if (matchingManagers.size > 0) {
              setSelectedManagers(Array.from(matchingManagers));
            }
          } else if (roleLen === 10 || roleLen === 13) {
            // Manager (10) or Market Center (13) level → default Manager filter
            const matchingManagers = new Set<string>();
            for (const role of roles) {
              const idField = roleLen === 10 ? 'grandparent_territory_id' : 'parent_territory_id';
              mapping.forEach((m: any) => {
                if (m[idField] === role && m.manager) {
                  matchingManagers.add(m.manager);
                }
              });
            }
            if (matchingManagers.size > 0) {
              setSelectedManagers(Array.from(matchingManagers));
            }
          }
          // For roleLen === 6 (e.g., "NATION"), no default — covers national-level roles
        }
        
      } catch (error: any) {
        console.error('Error loading initial data:', error);
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        setError(isTimeout 
          ? 'Request timed out after 60 seconds. The server may be under heavy load. Please try again.'
          : (error.message || 'Failed to load data. Please ensure the backend server is running.'));
      } finally {
        setInitialLoading(false);
      }
    };
    
    loadInitialData();
  }, [impersonatedRole, userRole]);

  // Handle drilldown from MMI Client Sourcing map
  useEffect(() => {
    if (!drilldownFilter || initialLoading) return;

    // Find the seller in allSellers (format: "SELLER_ID - SELLER_NAME")
    const matchingSeller = allSellers.find(s => s.startsWith(drilldownFilter.sellerId + ' - '));
    if (matchingSeller) {
      setSelectedSellers([matchingSeller]);
    } else {
      // Fallback: use seller name directly for the API filter
      setSelectedSellers([drilldownFilter.sellerId + ' - ' + drilldownFilter.sellerName]);
    }

    // Apply the action filter. Client View sends its chip keys underscored
    // (POTENTIAL_FOR_INSPECTION); the stage filter expects the spaced bucket name.
    // Every vin_action_filter bucket is now a valid Main view stage, so none are skipped.
    if (drilldownFilter.actionFilter) {
      setActiveActionFilter(drilldownFilter.actionFilter.replace(/_/g, ' ').toUpperCase());
    } else {
      setActiveActionFilter(null);
    }

    // Reset page
    setCurrentPage(1);

    // Signal that drilldown has been consumed
    onDrilldownConsumed?.();
  }, [drilldownFilter, initialLoading, allSellers, onDrilldownConsumed]);

  // Fetch vehicles when filters change (skip during initial load to prevent race condition)
  // During initial load, default filters haven't been applied yet, so fetching would
  // return unfiltered results that could overwrite the correctly-filtered results.
  useEffect(() => {
    // Don't fetch until initial data (filter options + defaults) is loaded.
    // This prevents a race condition where an unfiltered fetch fires before
    // defaults are applied, and its slower response overwrites the filtered results.
    if (initialLoading) return;

    const abortController = new AbortController();

    const fetchVehicles = async () => {
      try {
        setIsDataLoading(true);
        const skip = (currentPage - 1) * recordsPerPage;
        
        // Build sort params from sortColumns array
        const sortByParam = sortColumns.map(s => s.key).join(',');
        const sortOrderParam = sortColumns.map(s => s.order).join(',');
        
        const params: any = { 
          limit: recordsPerPage,
          skip: skip,
          sort_by: sortByParam,
          sort_order: sortOrderParam
        };
        
        // Add gating filters (now arrays joined by comma)
        if (selectedManagers.length > 0) {
          params.manager = selectedManagers.join(',');
        }
        
        if (selectedMarketCenters.length > 0) {
          params.market_center = selectedMarketCenters.join(',');
        }

        // Add optional RDS Territory filter
        if (selectedRdsTerritories.length > 0) {
          params.rds_territory = selectedRdsTerritories.join(',');
        }
        
        if (selectedSellers.length > 0) {
          // Extract seller_names from "SELLER_ID - SELLER_NAME" format and join
          const sellerNames = selectedSellers.map(s => {
            const dashIndex = s.indexOf(' - ');
            return dashIndex > -1 ? s.substring(dashIndex + 3) : s;
          });
          params.seller_name = sellerNames.join(',');
        }

        if (activeActionFilter) {
          params.stage = activeActionFilter;
        }

        if (activeSearch) {
          params.search = activeSearch;
        }

        // Add aging range filter to backend params (comma-separated for multi-select)
        if (selectedAgingRanges.length > 0) {
          params.days_listed_ranges = selectedAgingRanges.join(',');
        }

        // Add expired range filter to backend params (comma-separated for multi-select)
        if (selectedExpiredRanges.length > 0) {
          params.days_expired_ranges = selectedExpiredRanges.join(',');
        }

        // Add days since inspection range filter (comma-separated for multi-select)
        if (selectedDaysSinceInspectionRanges.length > 0) {
          params.days_since_inspection_ranges = selectedDaysSinceInspectionRanges.join(',');
        }

        // Add inspect status filter (comma-separated for multi-select)
        if (selectedInspectStatuses.length > 0) {
          params.inspect_status = selectedInspectStatuses.join(',');
        }

        // Add user status filter
        if (selectedUserStatuses.length > 0) {
          params.user_status = selectedUserStatuses.join(',');
        }

        // Add column filters from table column dropdowns
        const hasColumnFilters = Object.keys(columnFilters).some(key => columnFilters[key].size > 0);
        if (hasColumnFilters) {
          // Convert Record<string, Set<string>> to Record<string, string[]> for JSON
          const columnFiltersObj: Record<string, string[]> = {};
          for (const [key, values] of Object.entries(columnFilters)) {
            if (values.size > 0) {
              columnFiltersObj[key] = Array.from(values);
            }
          }
          params.column_filters = JSON.stringify(columnFiltersObj);
        }

        const response = await siftApiClient.getVehicles(params, impersonatedRole || undefined);
        
        // If this request was superseded by a newer one, discard the stale response
        if (abortController.signal.aborted) return;
        
        const vehicles = apiVehiclesToSIFTVehicles(response.vehicles);
        setSiftVehicles(vehicles);
        setTotalRecords(response.total);
        setVinDataUpdated(response.vin_data_last_updated ?? null);
        if (response.filter_counts) {
          setFilterCounts(response.filter_counts);
        }
        
      } catch (error: any) {
        // Ignore errors from aborted requests
        if (abortController.signal.aborted) return;
        console.error('Error fetching vehicles:', error);
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        setError(isTimeout 
          ? 'Request timed out after 60 seconds. The server may be under heavy load. Please try again.'
          : (error.message || 'Failed to fetch vehicles.'));
      } finally {
        if (!abortController.signal.aborted) {
          setIsDataLoading(false);
        }
      }
    };

    fetchVehicles();

    // Cleanup: abort the in-flight request if deps change before it completes
    return () => {
      abortController.abort();
    };
  }, [initialLoading, selectedManagers, selectedMarketCenters, selectedRdsTerritories, selectedSellers, activeActionFilter, selectedAgingRanges, selectedExpiredRanges, selectedDaysSinceInspectionRanges, selectedInspectStatuses, selectedUserStatuses, currentPage, sortColumns, activeSearch, columnFilters, impersonatedRole]);

  // Handler for column filter changes from VehicleDataTable
  const handleColumnFiltersChange = (newFilters: Record<string, Set<string>>) => {
    flushSync(() => setIsDataLoading(true));
    setColumnFilters(newFilters);
    setCurrentPage(1); // Reset to first page when column filters change
  };

  // Simplified handlers since we're removing chat and modal functionality
  const handleRowSelect = (_vehicles: SIFTVehicle[]) => {
    // Placeholder for future functionality
    void _vehicles;
  };

  // Apply client-side filters (not needed now that all filters are backend)
  useEffect(() => {
    setFilteredVehicles([...siftVehicles]);
  }, [siftVehicles]);

  const handleManagerChange = (managers: string[]) => {
    flushSync(() => setIsDataLoading(true));
    setSelectedManagers(managers);
    setCurrentPage(1);
  };

  const handleMarketCenterChange = (marketCenters: string[]) => {
    flushSync(() => setIsDataLoading(true));
    setSelectedMarketCenters(marketCenters);
    setCurrentPage(1);
  };

  const handleRdsTerritoryChange = (territories: string[]) => {
    flushSync(() => setIsDataLoading(true));
    setSelectedRdsTerritories(territories);
    setCurrentPage(1);
  };

  const handleSellerChange = (sellers: string[]) => {
    flushSync(() => setIsDataLoading(true));
    setSelectedSellers(sellers);
    setCurrentPage(1);
  };


  const handleActionFilterClick = (actionFilter: typeof ACTION_FILTERS[0]) => {
    flushSync(() => setIsDataLoading(true));
    setCurrentPage(1); // Reset to first page when filter changes
    if (activeActionFilter === actionFilter.key) {
      // Clear filter if clicking the same group
      setActiveActionFilter(null);
    } else {
      // Apply new filter
      setActiveActionFilter(actionFilter.key);
    }
  };

  const handleAgingFilterClick = (range: string) => {
    flushSync(() => setIsDataLoading(true));
    setCurrentPage(1); // Reset to first page when filter changes
    setSelectedAgingRanges(prev => 
      prev.includes(range) ? prev.filter(r => r !== range) : [...prev, range]
    );
  };

  const handleExpiredFilterClick = (range: string) => {
    flushSync(() => setIsDataLoading(true));
    setCurrentPage(1); // Reset to first page when filter changes
    setSelectedExpiredRanges(prev => 
      prev.includes(range) ? prev.filter(r => r !== range) : [...prev, range]
    );
  };

  const handleDaysSinceInspectionFilterClick = (range: string) => {
    flushSync(() => setIsDataLoading(true));
    setCurrentPage(1); // Reset to first page when filter changes
    setSelectedDaysSinceInspectionRanges(prev => 
      prev.includes(range) ? prev.filter(r => r !== range) : [...prev, range]
    );
  };

  const handleInspectStatusFilterClick = (status: string) => {
    flushSync(() => setIsDataLoading(true));
    setCurrentPage(1); // Reset to first page when filter changes
    setSelectedInspectStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleUserStatusFilterClick = (status: string) => {
    flushSync(() => setIsDataLoading(true));
    setCurrentPage(1); // Reset to first page when filter changes
    setSelectedUserStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  // Handle status assignment from table
  const handleStatusAssign = async (vins: string[], status: UserVinStatus, reasonText?: string | null) => {
    try {
      if (status === null) {
        // Clear status - delete for each VIN
        // Note: The API handles bulk delete via query param
        await siftApiClient.deleteVinStatusBulk(vins);
      } else {
        // Set status
        await siftApiClient.setVinStatusBulk(vins, status, reasonText);
      }
      
      // Clear selection after successful assignment
      setSelectedVins(new Set());
      
      // Refresh vehicle data to show updated statuses
      // We need to re-fetch from the API since statuses are enriched server-side
      setIsDataLoading(true);
      try {
        const skip = (currentPage - 1) * recordsPerPage;
        const sortByParam = sortColumns.map(s => s.key).join(',');
        const sortOrderParam = sortColumns.map(s => s.order).join(',');
        
        // Build params using the same pattern as fetchVehicles
        const params: Record<string, string | number> = { 
          limit: recordsPerPage,
          skip: skip,
          sort_by: sortByParam,
          sort_order: sortOrderParam
        };
        
        // Rebuild all the filter params
        if (selectedManagers.length > 0) params.manager = selectedManagers.join(',');
        if (selectedMarketCenters.length > 0) params.market_center = selectedMarketCenters.join(',');
        if (selectedRdsTerritories.length > 0) params.rds_territory = selectedRdsTerritories.join(',');
        if (selectedSellers.length > 0) {
          const sellerNames = selectedSellers.map(s => {
            const dashIndex = s.indexOf(' - ');
            return dashIndex > -1 ? s.substring(dashIndex + 3) : s;
          });
          params.seller_name = sellerNames.join(',');
        }
        if (activeActionFilter) params.stage = activeActionFilter;
        if (activeSearch) params.search = activeSearch;
        if (selectedAgingRanges.length > 0) params.days_listed_ranges = selectedAgingRanges.join(',');
        if (selectedExpiredRanges.length > 0) params.days_expired_ranges = selectedExpiredRanges.join(',');
        if (selectedDaysSinceInspectionRanges.length > 0) params.days_since_inspection_ranges = selectedDaysSinceInspectionRanges.join(',');
        if (selectedInspectStatuses.length > 0) params.inspect_status = selectedInspectStatuses.join(',');
        if (selectedUserStatuses.length > 0) params.user_status = selectedUserStatuses.join(',');
        
        const hasColumnFilters = Object.keys(columnFilters).some(key => columnFilters[key].size > 0);
        if (hasColumnFilters) {
          const columnFiltersObj: Record<string, string[]> = {};
          for (const [key, values] of Object.entries(columnFilters)) {
            if (values.size > 0) columnFiltersObj[key] = Array.from(values);
          }
          params.column_filters = JSON.stringify(columnFiltersObj);
        }
        
        const response = await siftApiClient.getVehicles(params, impersonatedRole || undefined);
        const vehicles = apiVehiclesToSIFTVehicles(response.vehicles);
        setSiftVehicles(vehicles);
        setTotalRecords(response.total);
        if (response.filter_counts) setFilterCounts(response.filter_counts);
      } finally {
        setIsDataLoading(false);
      }
      
    } catch (error) {
      console.error('Failed to assign status:', error);
      setIsDataLoading(false);
      // Could show error toast here
    }
  };

  const handleSearchClick = () => {
    flushSync(() => setIsDataLoading(true));
    setCurrentPage(1); // Reset to first page when search is applied
    setActiveSearch(searchTerm);
  };

  const handleClearSearch = () => {
    flushSync(() => setIsDataLoading(true));
    setSearchTerm('');
    setActiveSearch('');
    setCurrentPage(1);
  };

  // Get aging range counts based on current filters (excluding aging filter)
  const getAgingRangeCounts = () => {
    return filterCounts?.days_listed_counts || {
      '0': 0,
      '1-7': 0,
      '8-14': 0,
      '15-30': 0,
      '31+': 0
    };
  };

  // Get expired range counts based on current filters (excluding expired filter)
  const getExpiredRangeCounts = () => {
    return filterCounts?.days_expired_counts || {
      '0': 0,
      '1-7': 0,
      '8-14': 0,
      '15+': 0
    };
  };

  // Get days since inspection counts (only Inspected units, excluding days_since_inspection filter)
  const getDaysSinceInspectionRangeCounts = () => {
    return filterCounts?.days_since_inspection_counts || {
      '0': 0,
      '1-7': 0,
      '8-14': 0,
      '15+': 0
    };
  };

  // Get inspect status counts based on current filters
  const getInspectStatusCounts = () => {
    return filterCounts?.inspect_status_counts || {
      'Inspected': 0,
      'Not Inspected': 0
    };
  };

  // Get user status counts based on current filters
  const getUserStatusCounts = () => {
    return filterCounts?.user_status_counts || {
      'In Progress': 0,
      'Done': 0,
      'Sold / NA': 0,
      'Updated': 0,
      'Reinspection Needed': 0,
      'Delete': 0,
      'CR Reuse': 0,
      'For Auction': 0,
      'Client Elect, No List': 0,
      'Unassigned': 0
    };
  };

  // Calculate counts for each action filter based on current filters (excluding action filter)
  const getActionFilterCount = (actionFilter: typeof ACTION_FILTERS[0]) => {
    return filterCounts?.stage_counts?.[actionFilter.key] || 0;
  };

  if (initialLoading) {
    return (
      <div className="unified-dashboard">
        <div className="dashboard-main">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading SIFT data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="unified-dashboard">
        <div className="dashboard-main">
          <div className="error-state" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '3rem',
            color: 'var(--text-primary)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h2 style={{ marginBottom: '1rem' }}>Error Loading Data</h2>
            <p style={{ marginBottom: '1.5rem', textAlign: 'center', maxWidth: '500px' }}>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--accent-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-dashboard">
      {/* Loading Overlay - Shows when data is loading from filter changes */}
      {isDataLoading && (
        <div className="loading-overlay">
          <div className="loading-overlay-content">
            <div className="loading-spinner"></div>
            <p className="loading-message">Loading vehicles...</p>
            <p className="loading-submessage">Applying your filter selections</p>
          </div>
        </div>
      )}
      
      {/* Main Content Area */}
      <div className={`dashboard-main ${isDataLoading ? 'is-loading' : ''}`}>
        {/* Filter Pane - Dropdown Filters and Action Filters */}
        <div className={`filter-pane ${isFilterPaneCollapsed ? 'collapsed' : ''}`}>
          <div
            className="ud-data-freshness"
            title={`VIN data last updated ${formatIngestTs(vinDataUpdated)}`}
          >
            VIN <strong>{formatIngestAge(vinDataUpdated)}</strong>
          </div>
          {/* Gating Dropdown Filters - 2 columns: Manager/Market Center | RDS Territory/Seller */}
          <div className="dropdown-filters">
            {/* Column 1, Row 1: Manager Filter */}
            <MultiSelectDropdown
              label="Manager"
              options={filteredOptions.managers}
              values={selectedManagers}
              onChange={handleManagerChange}
              placeholder="Select Managers..."
              disabled={isDataLoading}
            />
            {/* Column 2, Row 1: RDS Territory Filter */}
            <MultiSelectDropdown
              label="RDS Territory"
              options={filteredOptions.rdsTerritories}
              values={selectedRdsTerritories}
              onChange={handleRdsTerritoryChange}
              placeholder="Select Territories..."
              disabled={isDataLoading}
            />
            {/* Column 1, Row 2: Market Center Filter */}
            <MultiSelectDropdown
              label="Market Center"
              options={filteredOptions.marketCenters}
              values={selectedMarketCenters}
              onChange={handleMarketCenterChange}
              placeholder="Select Market Centers..."
              disabled={isDataLoading}
            />
            {/* Column 2, Row 2: Seller Filter */}
            <MultiSelectDropdown
              label="Seller"
              options={filteredOptions.sellers}
              values={selectedSellers}
              onChange={handleSellerChange}
              placeholder="Select Sellers..."
              disabled={isDataLoading}
            />
          </div>

          {/* Action Filters - Always visible */}
          <div className="filter-content">
            <div className={`action-filters-simple ${isDataLoading ? 'loading' : ''}`}>
              {ACTION_FILTERS.map((actionFilter) => {
                const count = getActionFilterCount(actionFilter);
                const isActive = activeActionFilter === actionFilter.key;
                
                return (
                  <div 
                    key={actionFilter.key}
                    className={`action-filter-btn ${isActive ? 'active' : ''} ${isDataLoading ? 'disabled' : ''}`}
                    onClick={() => !isDataLoading && handleActionFilterClick(actionFilter)}
                    style={{ '--filter-color': actionFilter.color } as React.CSSProperties}
                    title={actionFilter.label}
                  >
                    <div className="action-filter-label">{actionFilter.label}</div>
                    <div className="action-filter-count">{count}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Collapse top filters - inside the pane, far right */}
          <button
            className="filter-pane-toggle"
            onClick={() => setIsFilterPaneCollapsed(true)}
            title="Hide top filters"
          >
            ▲
          </button>
        </div>

        {/* Show top filters button - only visible when collapsed */}
        {isFilterPaneCollapsed && (
          <button
            className="filter-pane-show-btn"
            onClick={() => setIsFilterPaneCollapsed(false)}
            title="Show top filters"
          >
            ▼ Show Filters
          </button>
        )}

        {/* Content Area with Sidebar and Table - Always visible */}
        <div className="dashboard-content-wrapper">
          {/* Show sidebar button - overlaid when collapsed */}
          {isSidebarCollapsed && (
            <button
              className="sidebar-show-btn"
              onClick={() => setIsSidebarCollapsed(false)}
              title="Show sidebar"
            >
              ▶
            </button>
          )}
          {/* Left Sidebar Filters */}
          <div className={`sidebar-filters ${isDataLoading ? 'loading' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            {/* Collapse sidebar button - inside sidebar at top */}
            <button
              className="sidebar-toggle-btn"
              onClick={() => setIsSidebarCollapsed(true)}
              title="Hide sidebar"
            >
              ◀
            </button>
            {/* User Status Filter - My Status at top */}
            <div className="filter-section">
              <h3 className="filter-section-title">My Status</h3>
              <div className="filter-options">
                {Object.entries(getUserStatusCounts()).map(([status, count]) => (
                  <div 
                    key={status}
                    className={`filter-option ${selectedUserStatuses.includes(status) ? 'active' : ''} ${isDataLoading ? 'disabled' : ''}`}
                    onClick={() => !isDataLoading && handleUserStatusFilterClick(status)}
                  >
                    <span className="filter-option-label">{status}</span>
                    <span className="filter-option-count">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Inspect Status Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Inspect Status</h3>
              <div className="filter-options">
                {Object.entries(getInspectStatusCounts()).map(([status, count]) => (
                  <div 
                    key={status}
                    className={`filter-option ${selectedInspectStatuses.includes(status) ? 'active' : ''} ${isDataLoading ? 'disabled' : ''}`}
                    onClick={() => !isDataLoading && handleInspectStatusFilterClick(status)}
                  >
                    <span className="filter-option-label">{status}</span>
                    <span className="filter-option-count">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Days Since Inspection Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Days Since Inspection</h3>
              <div className="filter-options">
                {Object.entries(getDaysSinceInspectionRangeCounts()).map(([range, count]) => (
                  <div 
                    key={range}
                    className={`filter-option ${selectedDaysSinceInspectionRanges.includes(range) ? 'active' : ''} ${isDataLoading ? 'disabled' : ''}`}
                    onClick={() => !isDataLoading && handleDaysSinceInspectionFilterClick(range)}
                  >
                    <span className="filter-option-label">{range} days</span>
                    <span className="filter-option-count">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Aging Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Days Listed</h3>
              <div className="filter-options">
                {Object.entries(getAgingRangeCounts()).map(([range, count]) => (
                  <div 
                    key={range}
                    className={`filter-option ${selectedAgingRanges.includes(range) ? 'active' : ''} ${isDataLoading ? 'disabled' : ''}`}
                    onClick={() => !isDataLoading && handleAgingFilterClick(range)}
                  >
                    <span className="filter-option-label">{range} days</span>
                    <span className="filter-option-count">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Days Expired Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Days Expired</h3>
              <div className="filter-options">
                {Object.entries(getExpiredRangeCounts()).map(([range, count]) => (
                  <div 
                    key={range}
                    className={`filter-option ${selectedExpiredRanges.includes(range) ? 'active' : ''} ${isDataLoading ? 'disabled' : ''}`}
                    onClick={() => !isDataLoading && handleExpiredFilterClick(range)}
                  >
                    <span className="filter-option-label">{range} days</span>
                    <span className="filter-option-count">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Clear Filters Button */}
            {(selectedAgingRanges.length > 0 || selectedExpiredRanges.length > 0 || selectedDaysSinceInspectionRanges.length > 0 || selectedInspectStatuses.length > 0 || selectedUserStatuses.length > 0) && (
              <button 
                className="clear-filters-btn"
                onClick={() => {
                  setSelectedAgingRanges([]);
                  setSelectedExpiredRanges([]);
                  setSelectedDaysSinceInspectionRanges([]);
                  setSelectedInspectStatuses([]);
                  setSelectedUserStatuses([]);
                }}
              >
                Clear Sidebar Filters
              </button>
            )}
          </div>

          {/* Data Table */}
          <div className={`dashboard-content ${isDataLoading ? 'loading' : ''}`}>
            {isDataLoading ? (
              <div className="table-loading-state">
                <div className="loading-spinner"></div>
                <p>Loading vehicles...</p>
              </div>
            ) : (
              <VehicleDataTable 
                miaEnabled={miaEnabled}
                vehicles={filteredVehicles}
                onRowSelect={handleRowSelect}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearchClick={handleSearchClick}
                onClearSearch={activeSearch ? handleClearSearch : undefined}
                sortColumns={sortColumns}
                onSortChange={(columns) => {
                  setIsDataLoading(true); // Show loading immediately
                  setSortColumns(columns);
                  setCurrentPage(1); // Reset to page 1 when sorting changes
                }}
                currentFilters={{
                  manager: selectedManagers.length > 0 ? selectedManagers.join(',') : undefined,
                  market_center: selectedMarketCenters.length > 0 ? selectedMarketCenters.join(',') : undefined,
                  rds_territory: selectedRdsTerritories.length > 0 ? selectedRdsTerritories.join(',') : undefined,
                  seller_name: selectedSellers.length > 0 ? selectedSellers.join(',') : undefined,
                  stage: activeActionFilter || undefined,
                  inspect_status: selectedInspectStatuses.length > 0 ? selectedInspectStatuses.join(',') : undefined,
                  days_listed_ranges: selectedAgingRanges.length > 0 ? selectedAgingRanges.join(',') : undefined,
                  days_expired_ranges: selectedExpiredRanges.length > 0 ? selectedExpiredRanges.join(',') : undefined,
                  days_since_inspection_ranges: selectedDaysSinceInspectionRanges.length > 0 ? selectedDaysSinceInspectionRanges.join(',') : undefined,
                  user_status: selectedUserStatuses.length > 0 ? selectedUserStatuses.join(',') : undefined,
                  search: activeSearch || undefined,
                }}
                impersonateRole={impersonatedRole || undefined}
                columnFilters={columnFilters}
                onColumnFiltersChange={handleColumnFiltersChange}
                selectedVins={selectedVins}
                onSelectionChange={setSelectedVins}
                onStatusAssign={handleStatusAssign}
              />
            )}
            
            {/* Pagination Controls - Hide during loading */}
            {!isDataLoading && totalRecords > recordsPerPage && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Showing {((currentPage - 1) * recordsPerPage) + 1}-{Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords.toLocaleString()} records
                </div>
                <div className="pagination-buttons">
                  <button
                    className="pagination-btn"
                    onClick={() => { setIsDataLoading(true); setCurrentPage(1); }}
                    disabled={currentPage === 1}
                  >
                    &laquo; First
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => { setIsDataLoading(true); setCurrentPage(prev => Math.max(1, prev - 1)); }}
                    disabled={currentPage === 1}
                  >
                    &lsaquo; Prev
                  </button>
                  <span className="pagination-page-info">
                    Page {currentPage} of {Math.ceil(totalRecords / recordsPerPage)}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => { setIsDataLoading(true); setCurrentPage(prev => Math.min(Math.ceil(totalRecords / recordsPerPage), prev + 1)); }}
                    disabled={currentPage >= Math.ceil(totalRecords / recordsPerPage)}
                  >
                    Next &rsaquo;
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => { setIsDataLoading(true); setCurrentPage(Math.ceil(totalRecords / recordsPerPage)); }}
                    disabled={currentPage >= Math.ceil(totalRecords / recordsPerPage)}
                  >
                    Last &raquo;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};